import { useCallback, useEffect, useRef, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { CREATE_PROFILE_CREATION_AGENT_TASK, CANCEL_PROFILE_CREATION_AGENT_TASK } from "graphql/mutations";
import { GET_PROFILE_CREATION_AGENT_TASK } from "graphql/queries";
import useModalConfirmation from "hooks/useModalConfirmation";
import useProfile from "hooks/useProfile";
import { AGENT_TASK_STATE } from "constants/index";
import { FormattedMessage } from "react-intl";
import useToast from "hooks/useToast";

const POLL_INTERVAL = 5000;

export default function useProfileAutoFill(profileId) {
  const { toast } = useToast();
  const { show } = useModalConfirmation();

  const { data: profileData, loading: profileLoading } = useProfile({ profileId });

  const [task, setTask] = useState();
  const [pollingError, setPollingError] = useState();

  const pollingTaskIdRef = useRef();

  const [createProfileCreationAgentTask, { loading: creating }] = useMutation(CREATE_PROFILE_CREATION_AGENT_TASK);
  const [cancelProfileCreationAgentTask, { loading: canceling }] = useMutation(CANCEL_PROFILE_CREATION_AGENT_TASK);
  const [getProfileCreationAgentTask, { data: taskData, error: pollingQueryError, startPolling, stopPolling }] =
    useLazyQuery(GET_PROFILE_CREATION_AGENT_TASK, {
      fetchPolicy: "network-only",
    });

  const latestTask = profileData?.profile?.latestProfileCreationAgentTask;
  const fetchedTask = taskData?.profileCreationAgentTask;

  const stopTaskPolling = useCallback(() => {
    stopPolling();
    pollingTaskIdRef.current = null;
  }, [stopPolling]);

  const startTaskPolling = useCallback(
    async (taskId) => {
      if (!taskId) return;

      if (pollingTaskIdRef.current === taskId) return;

      if (pollingTaskIdRef.current) {
        stopPolling();
      }

      pollingTaskIdRef.current = taskId;

      try {
        await getProfileCreationAgentTask({
          variables: {
            externalTaskId: taskId,
          },
        });

        startPolling(POLL_INTERVAL);
      } catch (error) {
        pollingTaskIdRef.current = null;
        throw error;
      }
    },
    [getProfileCreationAgentTask, startPolling, stopPolling]
  );

  /*
   * Use the latest task returned with the profile as the source
   * of truth when the page initially loads/reloads.
   */
  useEffect(() => {
    if (profileLoading) return;

    setTask(latestTask);

    if (latestTask?.state === AGENT_TASK_STATE.processing) {
      startTaskPolling(latestTask.externalTaskId).catch(setPollingError);
    } else {
      stopTaskPolling();
    }
  }, [profileLoading, latestTask?.externalTaskId, latestTask?.state, startTaskPolling, stopTaskPolling]);

  const handleCreateProfileCreationAgentTask = async (resume) => {
    const confirmed = await show(
      <FormattedMessage id="useProfileAutoFill.ProfileCreationAgent.ConfirmTitle" />,
      <FormattedMessage id="useProfileAutoFill.ProfileCreationAgent.ConfirmDescription" />,
      {
        primaryButtonMessageId: "Global.Continue",
      }
    );

    if (!confirmed) return false;

    setPollingError(null);

    try {
      const { data } = await createProfileCreationAgentTask({
        variables: {
          input: {
            resume,
            profileId,
          },
        },
        onError: (error) =>
          toast.error(error.message || <FormattedMessage id="useProfileAutoFill.ProfileCreationAgent.Error" />),
      });

      const createdTask = data?.createProfileCreationAgentTask?.profileCreationAgentTask;

      if (!createdTask) return false;

      setTask(createdTask);

      if (createdTask.state === AGENT_TASK_STATE.processing) {
        await startTaskPolling(createdTask.externalTaskId);
      }

      return true;
    } catch {
      return false;
    }
  };

  const handleCancelProfileCreationAgentTask = async (externalTaskId) => {
    const confirmed = await show(
      <FormattedMessage id="useProfileAutoFill.ProfileCreationAgent.CancelConfirmTitle" />,
      <FormattedMessage id="useProfileAutoFill.ProfileCreationAgent.CancelConfirmDescription" />,
      {
        primaryButtonMessageId: "Global.Confirm",
      }
    );

    if (!confirmed) return false;

    setPollingError(null);

    try {
      const { data } = await cancelProfileCreationAgentTask({
        variables: {
          input: {
            externalTaskId,
          },
        },
        onError: () => toast.error(<FormattedMessage id="useProfileAutoFill.ProfileCreationAgent.CancelError" />),
      });

      const canceledTask = data?.cancelProfileCreationAgentTask?.profileCreationAgentTask;

      if (!canceledTask) return false;

      stopTaskPolling();
      setTask(canceledTask);

      return true;
    } catch {
      return false;
    }
  };

  /*
   * Update the task from each polling response.
   */
  useEffect(() => {
    if (!fetchedTask) return;

    setTask(fetchedTask);

    if ([AGENT_TASK_STATE.completed, AGENT_TASK_STATE.failed, AGENT_TASK_STATE.canceled].includes(fetchedTask.state)) {
      stopTaskPolling();
    }
  }, [fetchedTask, stopTaskPolling]);

  /*
   * A frontend/network error does not mean the backend task failed.
   * The background job continues independently.
   */
  useEffect(() => {
    if (!pollingQueryError) return;

    setPollingError(pollingQueryError);
    stopTaskPolling();
  }, [pollingQueryError, stopTaskPolling]);

  useEffect(() => {
    return () => stopTaskPolling();
  }, [stopTaskPolling]);

  const stepsCompleted = task?.progress?.stepsCompleted;
  const stepsTotal = task?.progress?.stepsTotal;

  const progress = task ? (stepsTotal > 0 ? Math.round((stepsCompleted / stepsTotal) * 100) : 0) : null;

  return {
    task,
    progress,
    resumeUrl: task?.resumeUrl,
    creating,
    canceling,
    profileLoading,
    pollingError,
    handleCreateProfileCreationAgentTask,
    handleCancelProfileCreationAgentTask,
  };
}
